import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { API_BASE } from "../lib/api";

export default function Balance8Columnas() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState(todayISO());

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data } = await api.get("/reportes/balance-8-columnas", { params });
      setData(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [desde, hasta]);

  const downloadFile = (format) => {
    const params = new URLSearchParams();
    if (desde) params.set("desde", desde);
    if (hasta) params.set("hasta", hasta);
    window.open(`${API_BASE}/reportes/balance-8-columnas/${format}?${params}`, "_blank");
  };

  return (
    <Page>
      <PageHeader
        title="Balance de 8 columnas"
        subtitle="Balance tributario chileno"
        testid="balance8-header"
        actions={
          <div className="flex items-center gap-2">
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="b8-desde"
              className="px-2 py-1.5 border border-[#E2DFD8] rounded-sm text-sm" placeholder="Desde" />
            <span className="text-[#59645E] text-xs">→</span>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="b8-hasta"
              className="px-2 py-1.5 border border-[#E2DFD8] rounded-sm text-sm" />
            <button onClick={() => downloadFile("excel")} data-testid="b8-excel"
              className="inline-flex items-center gap-1.5 border border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#2D6A4F]/10 text-sm px-3 py-1.5 rounded-sm">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </button>
            <button onClick={() => downloadFile("pdf")} data-testid="b8-pdf"
              className="inline-flex items-center gap-1.5 border border-[#A94438] text-[#A94438] hover:bg-[#A94438]/10 text-sm px-3 py-1.5 rounded-sm">
              <FileText className="h-4 w-4" /> PDF
            </button>
          </div>
        }
      />

      <div className="bg-white border border-[#E2DFD8] rounded-md overflow-auto">
        {loading || !data ? (
          <div className="p-8 text-center text-[#59645E] text-sm">Cargando…</div>
        ) : (
          <table className="acc-table" data-testid="b8-table" style={{ minWidth: 1200 }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ width: 40 }}>N°</th>
                <th rowSpan={2}>Cuenta</th>
                <th colSpan={2} className="text-center">Sumas</th>
                <th colSpan={2} className="text-center">Saldos</th>
                <th colSpan={2} className="text-center">Estado Patrimonial</th>
                <th colSpan={2} className="text-center">Estado de Resultados</th>
              </tr>
              <tr>
                <th className="num">Debe</th>
                <th className="num">Haber</th>
                <th className="num">Deudor</th>
                <th className="num">Acreedor</th>
                <th className="num">Activo</th>
                <th className="num">Pasivo+P.N.</th>
                <th className="num text-[#A94438]">R.N.(-) Pérdidas</th>
                <th className="num text-[#2D6A4F]">R.P.(+) Ganancias</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.codigo}>
                  <td className="num text-[12px]">{r.n}</td>
                  <td>
                    <span className="font-mono text-[12px] text-[#59645E] mr-2">{r.codigo}</span>
                    {r.cuenta}
                  </td>
                  <td className="num">{r.debe ? fmtCLP(r.debe) : "—"}</td>
                  <td className="num">{r.haber ? fmtCLP(r.haber) : "—"}</td>
                  <td className="num">{r.deudor ? fmtCLP(r.deudor) : "—"}</td>
                  <td className="num">{r.acreedor ? fmtCLP(r.acreedor) : "—"}</td>
                  <td className="num">{r.activo ? fmtCLP(r.activo) : "—"}</td>
                  <td className="num">{r.pasivo ? fmtCLP(r.pasivo) : "—"}</td>
                  <td className="num text-[#A94438]">{r.perdidas ? fmtCLP(r.perdidas) : "—"}</td>
                  <td className="num text-[#2D6A4F]">{r.ganancias ? fmtCLP(r.ganancias) : "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-[#F0EDE6] font-semibold">
                <td></td>
                <td className="label-caps">Subtotales</td>
                <td className="num">{fmtCLP(data.subtotales.debe)}</td>
                <td className="num">{fmtCLP(data.subtotales.haber)}</td>
                <td className="num">{fmtCLP(data.subtotales.deudor)}</td>
                <td className="num">{fmtCLP(data.subtotales.acreedor)}</td>
                <td className="num">{fmtCLP(data.subtotales.activo)}</td>
                <td className="num">{fmtCLP(data.subtotales.pasivo)}</td>
                <td className="num text-[#A94438]">{fmtCLP(data.subtotales.perdidas)}</td>
                <td className="num text-[#2D6A4F]">{fmtCLP(data.subtotales.ganancias)}</td>
              </tr>
              <tr className="bg-[#F0EDE6]">
                <td></td>
                <td className="label-caps text-[#A94438]">Resultado del ejercicio</td>
                <td></td><td></td><td></td><td></td><td></td>
                <td className="num font-semibold">{fmtCLP(data.resultado_ejercicio.utilidad)}</td>
                <td className="num font-semibold text-[#A94438]">{fmtCLP(data.resultado_ejercicio.perdida)}</td>
                <td></td>
              </tr>
              <tr className="bg-[#1B4332] text-white font-semibold">
                <td></td>
                <td className="label-caps text-white">Totales</td>
                <td className="num">{fmtCLP(data.totales.debe)}</td>
                <td className="num">{fmtCLP(data.totales.haber)}</td>
                <td className="num">{fmtCLP(data.totales.deudor)}</td>
                <td className="num">{fmtCLP(data.totales.acreedor)}</td>
                <td className="num">{fmtCLP(data.totales.activo)}</td>
                <td className="num">{fmtCLP(data.totales.pasivo)}</td>
                <td className="num">{fmtCLP(data.totales.perdidas)}</td>
                <td className="num">{fmtCLP(data.totales.ganancias)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </Page>
  );
}
